/**
 * Canonical web PWA origin (unified capture + execute + follow).
 * Keep in sync with functions/src/webOrigins.js and
 * apps/mobile/constants/webOrigins.js.
 */

export const CANONICAL_WEB_ORIGIN = 'https://my.x9.app';

/** Marketing / install landing (Get X9 app). */
export const MARKETING_SITE_URL = 'https://x9.app/';

/** Hosts accepted when parsing scanned QR payloads / deep links. */
export const WEB_APP_HOSTS = new Set<string>([
  'use.x9.app',
  'x9-app.web.app',
  'my.x9.app',
  'x9-my.web.app',
  'x9app-51a91.web.app',
  'x9app-51a91.firebaseapp.com',
]);

/** Path prefixes that must never be treated as bare capture tokens. */
export const RESERVED_PATH_PREFIXES = new Set<string>([
  't',
  'f',
  'q',
  'r',
  'home',
  'routines',
  'buckets',
  'contacts',
  'find',
  'activity',
  'settings',
  'design',
  'icons',
  'assets',
  'src',
  'signin',
  'checkout',
  'account',
  'seats',
  'pending',
  'email',
  'verify-email',
  'usage',
  'go',
  'pricing',
  'billing',
  'plan',
  'payment',
  'history',
]);

export function captureTokenUrl(token: string): string {
  return `${CANONICAL_WEB_ORIGIN}/q/${encodeURIComponent(token)}`;
}

export function taskExecuteUrl(shortId: string): string {
  return `${CANONICAL_WEB_ORIGIN}/t/${encodeURIComponent(shortId)}`;
}

export function taskFollowUrl(shortId: string): string {
  return `${CANONICAL_WEB_ORIGIN}/f/${encodeURIComponent(shortId)}`;
}

/** Routine run deep link (PDF QR / FR11). Legacy `/r/run_…` native gateway. */
export function runDeepLinkUrl(runId: string): string {
  return `${CANONICAL_WEB_ORIGIN}/r/${encodeURIComponent(runId)}`;
}

/** Execute link for an external run (`runLinks/{shortId}`). */
export function runLinkUrl(shortId: string): string {
  return `${CANONICAL_WEB_ORIGIN}/r/${encodeURIComponent(shortId)}`;
}

/** `createRoutineRun` ids are `run_<base36>_<6>` — those stay the native gateway. */
export function isLegacyRunDeepLinkId(segment: string): boolean {
  return typeof segment === 'string' && segment.startsWith('run_');
}

const MY_WEB_HOSTS = new Set(['my.x9.app', 'x9-my.web.app']);

/** True on the billing+work clone (`my.x9.app`), including its local Vite port. */
export function isMyWebHost(
  hostname = typeof window === 'undefined' ? '' : window.location.hostname,
  port = typeof window === 'undefined' ? '' : window.location.port,
): boolean {
  const host = hostname.toLowerCase();
  if (MY_WEB_HOSTS.has(host)) return true;
  return (host === 'localhost' || host === '127.0.0.1') && (port === '5176' || port === '4176');
}
